import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from "react";
import { computeAnchoredPosition } from "../utils/anchoredPosition";

export interface UsePopoverPositionOptions {
  /** The control the panel hangs off. */
  triggerRef: RefObject<HTMLElement | null>;
  /** The portalled panel itself, measured once mounted. */
  panelRef: RefObject<HTMLElement | null>;
  /** Whether the panel is currently rendered. */
  isOpen: boolean;
  /**
   * Panel width, so the horizontal clamp matches the stylesheet. Omit for a panel that sizes
   * itself off its content or its trigger — it is then measured once mounted, falling back to
   * the trigger's own width for the first paint.
   */
  width?: number;
  /**
   * First-paint height estimate. The real height is measured once the panel mounts —
   * guessing alone is how a picker ended up hanging off the bottom of the viewport.
   */
  heightEstimate?: number;
}

/**
 * Viewport-clamped fixed position for a panel portalled to `document.body`.
 *
 * Portalling is what makes this necessary: absolute positioning inside the field is clipped
 * by any scrolling ancestor, which is what sliced the month header and the Done button off
 * the date picker inside the call-list builder's scrolling modal body. Once portalled, the
 * panel has to be positioned from the trigger's viewport rect and repositioned on scroll and
 * resize — shared here so every portalled picker clamps identically instead of each one
 * re-deriving the arithmetic.
 *
 * Returns null until a position is known; render the panel only once it is non-null.
 */
export const usePopoverPosition = ({
  triggerRef,
  panelRef,
  isOpen,
  width,
  heightEstimate,
}: UsePopoverPositionOptions) => {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    // Real size once mounted; the estimate and the trigger's width only cover the first paint.
    const height = panelRef.current?.offsetHeight || heightEstimate || 0;
    const panelWidth = width ?? (panelRef.current?.offsetWidth || rect.width);

    const { top, left } = computeAnchoredPosition({
      anchor: rect,
      panelWidth,
      panelHeight: height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });

    setPosition((previous) =>
      previous && previous.top === top && previous.left === left ? previous : { top, left }
    );
  }, [triggerRef, panelRef, width, heightEstimate]);

  // Re-measure against the mounted panel's true height. Runs before paint, so the corrected
  // position is what the user actually sees rather than a visible jump.
  //
  // This effect feeds its own `position` dependency, and terminates only because
  // `setPosition` above returns the previous object when top/left are unchanged. That
  // identity bail-out is load-bearing: a panel whose height depended on its position (a
  // max-height clamped to the space available, say) would turn this into a render loop.
  useLayoutEffect(() => {
    if (isOpen && panelRef.current) reposition();
  }, [isOpen, position, reposition, panelRef]);

  useEffect(() => {
    if (!isOpen) return;
    reposition();

    // Coalesced to one measurement per frame. The trigger often sits inside a scrolling
    // modal body, so an unthrottled handler re-measures — and re-renders the whole panel —
    // on every scroll event the browser emits.
    let frame: number | null = null;
    const handle = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        reposition();
      });
    };

    // Capture phase so a scroll on any ancestor is seen, not just the window.
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [isOpen, reposition]);

  return { position, reposition };
};
